import React from 'react';
import { Box, Typography, Chip, Tooltip, CircularProgress } from '@mui/material';
import { Toll as CreditIcon } from '@mui/icons-material';
import { useCredits } from '../hooks';

interface CreditBalanceProps {
  // Display variants
  variant?: 'chip' | 'full' | 'compact';
  // Optional: show loading state
  showLoading?: boolean;
  // Optional: callback when clicked
  onClick?: () => void;
  // Optional: use light colors for dark backgrounds (e.g., AppBar)
  lightMode?: boolean;
}

export const CreditBalance: React.FC<CreditBalanceProps> = ({
  variant = 'chip',
  showLoading = true,
  onClick,
  lightMode = false,
}) => {
  const { credits, isLoading } = useCredits();

  if (isLoading && showLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CircularProgress size={16} color="inherit" />
      </Box>
    );
  }

  // Show 0 credits if there's an error or credits haven't loaded
  // This ensures the UI is always consistent
  const creditData = credits || { availableCredits: 0, usedCredits: 0, totalCredits: 0 };

  const availableCredits = creditData.availableCredits;
  const hasCredits = availableCredits > 0;

  if (variant === 'chip') {
    // For light mode (dark backgrounds like AppBar), use filled chip with white/light colors
    if (lightMode) {
      return (
        <Tooltip title={`${availableCredits} board credit${availableCredits !== 1 ? 's' : ''} available`}>
          <Chip
            icon={<CreditIcon />}
            label={availableCredits}
            size="small"
            onClick={onClick}
            sx={{
              cursor: onClick ? 'pointer' : 'default',
              bgcolor: hasCredits ? 'rgba(255,255,255,0.15)' : 'warning.main',
              color: 'white',
              borderColor: 'rgba(255,255,255,0.3)',
              border: 1,
              '& .MuiChip-icon': {
                fontSize: '1rem',
                color: 'white',
              },
              '&:hover': {
                bgcolor: hasCredits ? 'rgba(255,255,255,0.25)' : 'warning.dark',
              },
            }}
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={`${availableCredits} board credit${availableCredits !== 1 ? 's' : ''} available`}>
        <Chip
          icon={<CreditIcon />}
          label={availableCredits}
          size="small"
          color={hasCredits ? 'primary' : 'warning'}
          variant="outlined"
          onClick={onClick}
          sx={{
            cursor: onClick ? 'pointer' : 'default',
            '& .MuiChip-icon': {
              fontSize: '1rem',
            },
          }}
        />
      </Tooltip>
    );
  }

  if (variant === 'compact') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: onClick ? 'pointer' : 'default',
        }}
        onClick={onClick}
      >
        <CreditIcon sx={{ fontSize: '1rem' }} />
        <Typography variant="body2">{availableCredits}</Typography>
      </Box>
    );
  }

  // Full variant
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CreditIcon color={hasCredits ? 'primary' : 'warning'} />
        <Typography variant="h6" component="span">
          Board Credits
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box>
          <Typography variant="h4" color={hasCredits ? 'primary.main' : 'warning.main'}>
            {availableCredits}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Available
          </Typography>
        </Box>
        <Box>
          <Typography variant="h4" color="text.secondary">
            {creditData.usedCredits}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Used
          </Typography>
        </Box>
        <Box>
          <Typography variant="h4" color="text.secondary">
            {creditData.totalCredits}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total
          </Typography>
        </Box>
      </Box>
      {!hasCredits && (
        <Typography variant="body2" color="warning.main">
          Purchase more credits to create boards
        </Typography>
      )}
    </Box>
  );
};
